import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import SEOHead from './common/SEOHead';

// Import mockup data
import { getFeaturedSalons, getAllSalons, searchSalons } from '../utils/mockupSalons';

// Import all the new components
import Header from './home/Header';
import HeroSection from './home/HeroSection';
import FeaturesSection from './home/FeaturesSection';
import ProfessionalSection from './home/ProfessionalSection';
import FeaturedSalons from './home/FeaturedSalons';
import TestimonialsSection from './home/TestimonialsSection';
import AboutSection from './home/AboutSection';
import HowItWorks from './home/HowItWorks';
import SearchSection from './home/SearchSection';
import CTASection from './home/CTASection';
import Footer from './home/Footer';

const HomePage = () => {
  const [salons, setSalons] = useState([]);
  const [featuredSalons, setFeaturedSalons] = useState([]);
  const [filteredSalons, setFilteredSalons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Utiliser les données mockup en priorité, puis essayer de récupérer les vraies données
    loadMockupData();
    fetchSalons();
    fetchFeaturedSalons();
  }, []);

  useEffect(() => {
    filterSalons();
  }, [searchTerm, selectedCategory, selectedLocation, salons]);

  const loadMockupData = () => {
    // Charger les données mockup immédiatement
    const mockupSalonsData = getAllSalons();
    const mockupFeaturedData = getFeaturedSalons();
    
    setSalons(mockupSalonsData);
    setFilteredSalons(mockupSalonsData);
    setFeaturedSalons(mockupFeaturedData);
    setLoading(false);
  };

  const fetchSalons = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'salon'),
        where('isPublic', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const salonsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.subscription && data.subscription.active) {
          salonsData.push({
            id: doc.id,
            ...data
          });
        }
      });
      
      // Si on a des vraies données, les combiner avec les mockup
      if (salonsData.length > 0) {
        const mockupData = getAllSalons();
        const combinedData = [...mockupData, ...salonsData];
        setSalons(combinedData);
        setFilteredSalons(combinedData);
      }
    } catch (error) {
      console.error('Error fetching salons:', error);
      // En cas d'erreur, garder les données mockup
    }
  };

  const fetchFeaturedSalons = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'salon'),
        where('isPublic', '==', true),
        orderBy('createdAt', 'desc'),
        limit(8)
      );
      
      const querySnapshot = await getDocs(q);
      const salonsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.subscription && data.subscription.active) {
          salonsData.push({
            id: doc.id,
            ...data,
            rating: Math.floor(Math.random() * 2) + 4, // Mock rating 4-5
            reviewCount: Math.floor(Math.random() * 50) + 10, // Mock review count
            // Ensure we have fallback data for display
            salonName: data.salonName || data.businessName || 'Salon de beauté',
            city: data.city || data.location || 'Ville',
            category: data.category || data.businessType || 'Beauté',
            photos: data.photos || [],
            description: data.description || data.bio || `Découvrez ${data.salonName || 'ce salon'} et ses services de qualité.`
          });
        }
      });

      // Combiner avec les données mockup
      const mockupFeatured = getFeaturedSalons();
      const combinedFeatured = [...mockupFeatured, ...salonsData];
      
      // Limiter à 8 salons maximum
      setFeaturedSalons(combinedFeatured.slice(0, 8));
    } catch (error) {
      console.error('Error fetching featured salons:', error);
      // En cas d'erreur, utiliser seulement les données mockup
      setFeaturedSalons(getFeaturedSalons());
    }
  };

  const filterSalons = () => {
    let filtered = salons;

    if (searchTerm) {
      filtered = filtered.filter(salon =>
        salon.salonName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        salon.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        salon.ownerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(salon =>
        salon.category === selectedCategory
      );
    }

    if (selectedLocation) {
      filtered = filtered.filter(salon => {
        // Handle both direct city property and address_components structure
        const selectedCity = selectedLocation.city || selectedLocation.address_components?.city || '';
        const selectedState = selectedLocation.state || selectedLocation.address_components?.state || '';
        
        if (!selectedCity && !selectedState) {
          // If no specific city/state, try to match against the full query
          const query = selectedLocation.query || '';
          return query ? (
            salon.city?.toLowerCase().includes(query.toLowerCase()) ||
            salon.region?.toLowerCase().includes(query.toLowerCase())
          ) : true;
        }
        
        const locationMatch =
          (selectedCity && salon.city?.toLowerCase().includes(selectedCity.toLowerCase())) ||
          (selectedState && salon.region?.toLowerCase().includes(selectedState.toLowerCase()));
        
        return locationMatch;
      });
    }

    setFilteredSalons(filtered);
  };

  const scrollToSearch = () => {
    document.getElementById('search-section').scrollIntoView({ behavior: 'smooth' });
  };

  // Generate structured data for the homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BookingBarber - Service de Réservation Barbier le Moins Cher au Monde",
    "description": "La plateforme de réservation barbier la plus abordable mondialement. Trouvez et réservez chez les meilleurs barbiers près de chez vous. Notre mission : aider les barbiers à prospérer avec des tarifs imbattables.",
    "url": "https://bookingbarber.fr",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://bookingbarber.fr/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://www.facebook.com/bookingbarber",
      "https://www.instagram.com/bookingbarber"
    ],
    "keywords": "bookingbarber, réservation barbier, service moins cher monde, barbier, coiffeur homme, shave, corte cheveux, plateforme barbier abordable"
  };

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "BookingBarber",
    "description": "La plateforme de réservation en ligne la moins chère au monde pour les barbiers. Notre mission : aider tous les barbiers à prospérer avec des tarifs accessibles.",
    "url": "https://bookingbarber.fr",
    "logo": "https://bookingbarber.fr/images/bookingbarber-logo.png",
    "slogan": "Le service de réservation barbier le moins cher au monde",
    "foundingDate": "2024",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "support@omnivers.fr",
      "contactType": "customer service",
      "availableLanguage": ["French"],
      "areaServed": "Worldwide"
    },
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Plateforme de Réservation Barbier",
        "description": "Service de réservation en ligne pour barbiers"
      },
      "price": "25",
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "25",
        "priceCurrency": "EUR",
        "billingIncrement": "monthly"
      }
    }
  };

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "BookingBarber - Plateforme de Réservation Barbier",
    "description": "Service de réservation en ligne le moins cher au monde pour les barbiers",
    "provider": {
      "@type": "Organization",
      "name": "BookingBarber"
    },
    "areaServed": "Worldwide",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://bookingbarber.fr",
      "serviceType": "Online booking platform"
    },
    "category": "Beauty Services",
    "offers": {
      "@type": "Offer",
      "price": "25",
      "priceCurrency": "EUR",
      "description": "Abonnement mensuel - Le plus abordable au monde"
    }
  };

  return (
    <>
      <SEOHead
        title="BookingBarber - Réservation Barbier : Corte Cheveux, Shave Traditionnel"
        description="🌟 BookingBarber : La plateforme de réservation barbier la moins chère au monde ! Trouvez les meilleurs barbiers près de chez vous : corte cheveux homme, shave traditionnel, rasage, coiffage, barbe. Réservation en ligne 24/7. Service le plus abordable mondialement."
        keywords="bookingbarber, plateforme réservation barbier, service moins cher monde, barbier, coiffeur homme, shave, corte cheveux, rasage, coiffage, barbe, réservation barbier en ligne, salon de barbier, barbier traditionnel, agenda automatisé barbier"
        canonicalUrl="https://bookingbarber.fr/"
        structuredData={[structuredData, organizationData, serviceData]}
      />
      <Box>
        <Header />
      
      <HeroSection
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
      />
      
      <FeaturesSection />
      
      <ProfessionalSection />
      
      <FeaturedSalons
        featuredSalons={featuredSalons}
        scrollToSearch={scrollToSearch}
      />
      
      <TestimonialsSection />
      
      <AboutSection />
      
      <HowItWorks />
      
      {/* <SearchSection
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        filteredSalons={filteredSalons}
      /> */}
      
      <CTASection />
      
      <Footer />
        </Box>
      </>
    );
  };

  export default HomePage;